import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, LayersControl, GeoJSON, useMap } from "react-leaflet";
import L from "leaflet";
import { Pause, Play } from "lucide-react";
import { faArrowCircleLeft, faArrowCircleRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconButton } from "@mui/material";
import "leaflet/dist/leaflet.css";
import "./SatelliteImagery.css";
import { MapHomeControl, MapInfoControl, glassStyle } from "../../../common/MapControls/MapControls";

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL || '';

// Tile/COG server. Note: dnmg-rtiles.metstudio.net shares an IP with ms-airflow.dnmg.gov.tl
// and serves that host's TLS cert (CN mismatch) — use ms-rtiles.dnmg.gov.tl, which is what
// the backend already uses for the himawari-9/info.json proxy.
const RTILES_BASE_URL = "https://ms-rtiles.dnmg.gov.tl";

// Timor-Leste center
const TIMOR_CENTER = [-8.8742, 125.7275];
const TIMOR_ZOOM = 8;

// MetStudio visible band colormap — surface/vegetation enhancement, matches staging.metstudio.net
const VIS_COLORMAP = encodeURIComponent(JSON.stringify([
    [[50,55],[0,0,50]],[[55,60],[0,0,40]],[[60,65],[0,0,30]],
    [[65,70],[0,0,20]],[[70,75],[0,0,10]],[[75,80],[0,20,0]],
    [[80,85],[0,30,0]],[[85,90],[0,40,0]],[[90,95],[0,50,0]],
    [[95,100],[0,60,0]],[[100,105],[0,70,0]],[[105,110],[0,80,0]],
    [[110,115],[20,90,20]],[[115,120],[40,100,40]],[[120,125],[60,110,60]],
    [[125,130],[100,80,60]],[[130,135],[120,100,80]],[[135,140],[140,120,100]],
    [[140,145],[160,140,120]],[[145,150],[180,160,140]],[[150,155],[200,180,120]],
    [[155,160],[220,200,100]],[[160,165],[240,220,80]],[[165,170],[255,240,60]],
    [[170,175],[255,255,40]],[[175,180],[200,200,200]],[[180,185],[215,215,215]],
    [[185,190],[230,230,230]],[[190,195],[240,240,240]],[[195,200],[245,245,245]],
    [[200,205],[248,248,248]],[[205,210],[250,250,250]],[[210,215],[252,252,252]],
    [[215,220],[254,254,254]],[[220,225],[255,255,255]],[[225,230],[255,255,255]],
    [[230,235],[255,255,255]],[[235,240],[255,255,255]],[[240,245],[255,255,255]],
    [[245,250],[255,255,255]],
]));

// MetStudio Water Vapor (Band 08) colormap — matches staging.metstudio.net
const WV_COLORMAP = encodeURIComponent(JSON.stringify([
    [[50,55],[139,69,19]],[[55,60],[160,82,45]],[[60,65],[205,133,63]],
    [[65,70],[222,184,135]],[[70,75],[255,140,0]],[[75,80],[255,165,0]],
    [[80,85],[255,215,0]],[[85,90],[255,255,0]],[[90,95],[173,255,47]],
    [[95,100],[127,255,0]],[[100,105],[50,205,50]],[[105,110],[0,255,127]],
    [[110,115],[0,255,255]],[[115,120],[0,191,255]],[[120,125],[30,144,255]],
    [[125,130],[0,0,255]],[[130,135],[0,0,205]],[[135,140],[0,0,139]],
    [[140,145],[25,25,112]],[[145,150],[72,61,139]],[[150,155],[106,90,205]],
    [[155,160],[138,43,226]],[[160,165],[148,0,211]],[[165,170],[153,50,204]],
    [[170,175],[186,85,211]],[[175,180],[221,160,221]],[[180,185],[238,130,238]],
    [[185,190],[255,192,203]],[[190,195],[255,182,193]],[[195,200],[255,160,122]],
    [[200,205],[255,127,80]],[[205,210],[255,99,71]],[[210,215],[255,69,0]],
    [[215,220],[255,0,0]],[[220,225],[220,20,60]],[[225,230],[178,34,34]],
    [[230,235],[139,0,0]],[[235,240],[128,0,0]],[[240,245],[75,0,130]],
    [[245,250],[25,25,25]],
]));

// MetStudio IR enhancement colormap — matches staging.metstudio.net
const IR_COLORMAP = encodeURIComponent(JSON.stringify([
    [[50,55],[255,255,255]],[[55,60],[248,248,255]],[[60,65],[240,248,255]],
    [[65,70],[230,230,250]],[[70,75],[220,220,220]],[[75,80],[211,211,211]],
    [[80,85],[192,192,192]],[[85,90],[169,169,169]],[[90,95],[128,128,128]],
    [[95,100],[105,105,105]],[[100,105],[70,130,180]],[[105,110],[100,149,237]],
    [[110,115],[65,105,225]],[[115,120],[0,0,255]],[[120,125],[0,0,205]],
    [[125,130],[0,100,0]],[[130,135],[0,128,0]],[[135,140],[0,255,0]],
    [[140,145],[0,255,127]],[[145,150],[0,255,255]],[[150,155],[255,255,0]],
    [[155,160],[255,215,0]],[[160,165],[255,165,0]],[[165,170],[255,140,0]],
    [[170,175],[255,69,0]],[[175,180],[255,0,0]],[[180,185],[220,20,60]],
    [[185,190],[178,34,34]],[[190,195],[139,0,0]],[[195,200],[128,0,0]],
    [[200,205],[105,0,0]],[[205,210],[80,0,0]],[[210,215],[60,0,0]],
    [[215,220],[40,0,0]],[[220,225],[20,0,0]],[[225,230],[10,0,0]],
    [[230,235],[5,0,0]],[[235,240],[0,0,0]],[[240,245],[0,0,0]],[[245,250],[0,0,0]],
]));

const TILE_CACHE = 'himawari-tiles-v1';

const TileLoadTracker = ({ onLoading, onLoaded }) => {
    const map = useMap();
    useEffect(() => {
        map.on('loading', onLoading);
        map.on('load', onLoaded);
        return () => {
            map.off('loading', onLoading);
            map.off('load', onLoaded);
        };
    }, [map, onLoading, onLoaded]);
    return null;
};

// Leaflet tile layer class that serves from the Cache API before hitting the
// network (the Himawari tile server has a 20-requests-per-minute limit).
// Shared by the visible layer (CachedTileLayer) and by FramePreloader, which
// silently warms every other frame's tiles into the same cache before
// playback starts.
const CachingTileLayer = L.TileLayer.extend({
    createTile(coords, done) {
        const tile = document.createElement('img');
        tile.setAttribute('crossOrigin', 'anonymous');
        const tileUrl = this.getTileUrl(coords);
        const layer = this;

        // The blob fetch below can resolve after this tile's layer has
        // already been removed from the map — FramePreloader does exactly
        // that once a frame's load either finishes or hits its safety
        // timeout. Calling Leaflet's `done()` at that point reaches into a
        // torn-down layer (`_map` is null) and throws. Once the layer is no
        // longer attached, there is nothing left to notify, so skip it.
        const safeDone = (err, t) => {
            if (!layer._map) return;
            done(err, t);
        };

        tile.onload = () => safeDone(null, tile);
        tile.onerror = (e) => safeDone(e, tile);

        const setFromBlob = (blob) => { tile.src = URL.createObjectURL(blob); };

        const fetchAndCache = (cache) =>
            fetch(tileUrl)
                .then(r => {
                    if (r.ok && cache) cache.put(tileUrl, r.clone());
                    return r.ok ? r.blob() : Promise.reject(r.status);
                })
                .then(setFromBlob)
                .catch(err => safeDone(err, tile));

        if (!('caches' in window)) { fetchAndCache(null); return tile; }

        caches.open(TILE_CACHE)
            .then(cache => cache.match(tileUrl)
                .then(hit => hit ? hit.blob().then(setFromBlob) : fetchAndCache(cache))
            )
            .catch(() => fetchAndCache(null));

        return tile;
    }
});

// How long the crossfade between two frames takes. Matched by the CSS
// transition on the .satimg-fade class every layer below is tagged with.
const FRAME_FADE_MS = 300;

// Leaflet tile layer that serves from Cache API before hitting the network
// (20 RPM limit), and crossfades from the previous frame into this one
// instead of a hard cut. The previous frame's layer is kept on screen —
// opacity 1 — until the new one has finished loading and faded in on top of
// it, so there is never a blank gap between "old frame gone" and "new frame
// visible"; then the old layer fades out underneath and is removed.
const CachedTileLayer = ({ url, attribution, opacity, zIndex }) => {
    const map = useMap();
    const currentLayerRef = useRef(null);

    useEffect(() => {
        if (!url) return undefined;

        let settled = false;
        const newLayer = new CachingTileLayer(url, {
            attribution, opacity: 0, zIndex, crossOrigin: 'anonymous', className: 'satimg-fade',
        });
        const prevLayer = currentLayerRef.current;

        const swapIn = () => {
            if (settled) return;
            settled = true;
            clearTimeout(fallbackTimer);
            currentLayerRef.current = newLayer;
            newLayer.setOpacity(opacity);          // fade the new frame in
            if (prevLayer) {
                prevLayer.setOpacity(0);            // fade the old frame out under it
                // Removing a layer from inside its own 'load' handler crashes
                // Leaflet (it reads this._map immediately after firing that
                // event) — prevLayer isn't the one that just fired 'load'
                // here, but keep every removal deferred one tick regardless,
                // so this stays safe under any future reordering too.
                setTimeout(() => map.removeLayer(prevLayer), FRAME_FADE_MS + 50);
            }
        };

        // Tiles are almost always already warmed by the preload pass, so
        // 'load' fires within a frame or two; the fallback just keeps a
        // never-preloaded / partially-failed view from staying invisible.
        newLayer.once('load', swapIn);
        const fallbackTimer = setTimeout(swapIn, 2500);
        map.addLayer(newLayer);

        return () => {
            clearTimeout(fallbackTimer);
            newLayer.off('load', swapIn);
            if (!settled) setTimeout(() => map.removeLayer(newLayer), 0);
        };
    }, [url, map, attribution, opacity, zIndex]);

    // Remove whatever is currently showing when this component itself unmounts.
    useEffect(() => () => {
        if (currentLayerRef.current) map.removeLayer(currentLayerRef.current);
    }, [map]);

    return null;
};

// Silently loads every frame's tiles, one frame at a time, at the map's
// current view, into the same Cache API CachedTileLayer reads from — before
// playback starts. Because the external tile server is comparatively slow
// and rate-limited, switching frames on demand during playback used to
// outrun the network; warming every frame up front means every frame is
// already on disk by the time playback actually steps through them, so it
// plays back as one smooth, continuous sequence instead of stalling.
const FramePreloader = ({ urls, active, onProgress, onComplete }) => {
    const map = useMap();
    useEffect(() => {
        if (!active) return undefined;
        let cancelled = false;

        const loadOne = (url) => new Promise((resolve) => {
            if (!url) { resolve(); return; }
            const layer = new CachingTileLayer(url, {
                opacity: 0, zIndex: -1, crossOrigin: 'anonymous', interactive: false,
            });
            let settled = false;
            const finish = () => {
                if (settled) return;
                settled = true;
                clearTimeout(safetyTimer);
                // Leaflet's own _tileReady fires this layer's 'load' event
                // and then, on its very next line, reads this._map on the
                // same layer — so removing the layer synchronously from
                // inside that same 'load' handler nulls _map while Leaflet
                // is still mid-call, and it crashes reading off null right
                // after. Deferring the removal lets that call finish first.
                setTimeout(() => map.removeLayer(layer), 0);
                resolve();
            };
            // 'load' fires once every tile currently on screen for this layer
            // has resolved; 'tileerror' must not be allowed to hang the whole
            // preload over one bad tile from an external, occasionally flaky
            // source, so any single frame gives up and moves on after 8s.
            layer.on('load', finish);
            const safetyTimer = setTimeout(finish, 8000);
            map.addLayer(layer);
        });

        (async () => {
            for (let i = 0; i < urls.length; i++) {
                if (cancelled) return;
                await loadOne(urls[i]);
                if (cancelled) return;
                onProgress(i + 1, urls.length);
            }
            if (!cancelled) onComplete(true);
        })();

        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, urls, map]);
    return null;
};

const formatTimestamp = (ts) => {
    if (!ts) return "--";
    const d = new Date(ts);
    if (!isNaN(d.getTime())) {
        return d.toLocaleString(undefined, {
            year: "numeric", month: "short", day: "2-digit",
            hour: "2-digit", minute: "2-digit", timeZone: "UTC", timeZoneName: "short",
        });
    }
    return String(ts);
};

// Floating "Satellite:" / "Layer:" picker — sits in the map's top-right
// corner, stacked below the built-in Satellite/OSM LayersControl (which
// occupies that same corner), so it never competes for page layout space.
const SatelliteFilterControl = ({
    satellites, selectedSatellite, onSatelliteChange,
    bands, selectedBand, onBandChange, selectStyle,
}) => {
    const noClickRef = useCallback((node) => {
        if (node) {
            L.DomEvent.disableClickPropagation(node);
            L.DomEvent.disableScrollPropagation(node);
        }
    }, []);

    const labelStyle = { fontWeight: 600, fontSize: "12px", color: "#333", whiteSpace: "nowrap" };

    return (
        <div className="leaflet-top leaflet-right" style={{ pointerEvents: "none" }}>
            <div
                ref={noClickRef}
                className="leaflet-control"
                style={{
                    pointerEvents: "auto",
                    marginTop: "10px",
                    marginRight: "60px",
                    padding: "10px 12px",
                    borderRadius: "10px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    ...glassStyle,
                    background: "rgba(255, 255, 255, 0.7)",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={labelStyle}>Satellite:</span>
                    <select
                        value={selectedSatellite}
                        onChange={onSatelliteChange}
                        style={{ ...selectStyle, minWidth: "120px" }}
                    >
                        {satellites.length === 0 && <option value="">Loading...</option>}
                        {satellites.map((sat, i) => (
                            <option key={i} value={sat}>{sat}</option>
                        ))}
                    </select>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={labelStyle}>Layer:</span>
                    <select
                        value={selectedBand}
                        onChange={onBandChange}
                        style={{ ...selectStyle, minWidth: "170px" }}
                    >
                        {Object.keys(bands).length === 0 && <option value="">Loading...</option>}
                        {Object.entries(bands).map(([key, band]) => (
                            <option key={key} value={key}>{band?.description || key}</option>
                        ))}
                    </select>
                </div>
            </div>
        </div>
    );
};

const SatelliteImagery = ({ embedded = false }) => {
    const [boundaryData, setBoundaryData] = useState(null);
    const [apiInfo, setApiInfo] = useState(null);
    const [selectedSatellite, setSelectedSatellite] = useState("");
    const [selectedBand, setSelectedBand] = useState("");
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [bandStats, setBandStats] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isTileLoading, setIsTileLoading] = useState(false);
    // Preload pass that runs once, right when Play is pressed, before actual
    // playback (isPlaying) begins — see FramePreloader above.
    const [isPreloading, setIsPreloading] = useState(false);
    const [preloadActive, setPreloadActive] = useState(false);
    const [preloadProgress, setPreloadProgress] = useState({ done: 0, total: 0 });
    // Bands whose tiles have already been fully preloaded this session, and
    // the key of whichever preload is currently in flight — both persist
    // across switching bands (unlike component state, refs don't reset the
    // moment a different band is selected), so coming back to a band that
    // was already warmed skips straight to playback.
    const preloadedKeysRef = useRef(new Set());
    const pendingPreloadKeyRef = useRef(null);
    const mapContainerRef = useRef(null);

    const handleTileLoading = useCallback(() => setIsTileLoading(true), []);
    const handleTileLoaded = useCallback(() => setIsTileLoading(false), []);

    // Fullscreen toggle
    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            mapContainerRef.current?.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    };

    // Listen for fullscreen change to sync state
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    useEffect(() => {
        fetch("/DATA/OSMbased_adm1_boundary.json")
            .then((res) => res.json())
            .then(setBoundaryData)
            .catch((err) => console.error("Boundary load error:", err));
    }, []);

    useEffect(() => {
        fetch(`${backendUrl}/cyclone/himawari-9/info/`)
            .then((res) => res.ok ? res.json() : null)
            .then((data) => {
                console.log("Himawari-9 API full response:", data);
                if (data) {
                    setApiInfo(data);
                    const satellites = Array.isArray(data.satellite)
                        ? data.satellite
                        : data.satellite ? [data.satellite] : [];
                    if (satellites.length > 0) setSelectedSatellite(satellites[0]);
                    const bands = data.bands || {};
                    console.log("Bands object:", bands);
                    const bandKeys = Object.keys(bands);
                    console.log("Band keys:", bandKeys);
                    if (bandKeys.length > 0) {
                        setSelectedBand(bandKeys[0]);
                        console.log("First band data:", bands[bandKeys[0]]);
                        console.log("First band timestamps:", bands[bandKeys[0]]?.timestamps);
                        console.log("First band full_path:", bands[bandKeys[0]]?.full_path);
                    }
                }
            })
            .catch((err) => console.error("Himawari-9 info fetch error:", err));
    }, []);

    // Reset index when band changes — also cancel any in-flight preload,
    // since it was warming tiles for the band being left behind.
    useEffect(() => {
        setCurrentIndex(0);
        setIsPlaying(false);
        setIsPreloading(false);
        setPreloadActive(false);
        setBandStats(null);
    }, [selectedBand]);

    // isTileLoading is read inside an async polling loop below (see the
    // play/pause effect), which needs its *current* value on every poll —
    // not the value captured when that effect last ran — hence the ref.
    const isTileLoadingRef = useRef(isTileLoading);
    useEffect(() => { isTileLoadingRef.current = isTileLoading; }, [isTileLoading]);

    // Play/pause — advances to the next timestamp only once the current
    // frame's tiles have actually finished loading, then holds it on screen
    // for a steady minimum duration. Previously this was a blind fixed
    // interval, so on a slow connection it would swap to the next timestamp
    // before the map had finished drawing the current one; this makes
    // playback flow like a video instead of jumping ahead of itself.
    useEffect(() => {
        if (!isPlaying) return undefined;

        const MIN_FRAME_MS = 900;   // steady, readable pace once a frame is ready
        const MAX_WAIT_MS  = 6000;  // don't let one slow/failed tile stall playback forever
        const POLL_MS      = 80;
        const SETTLE_MS    = 150;   // give the new tile layer time to start loading

        let cancelled = false;
        let timeoutId;

        const wait = (ms) => new Promise((resolve) => { timeoutId = setTimeout(resolve, ms); });

        const waitForFrameReady = async () => {
            const startedAt = Date.now();
            await wait(SETTLE_MS);
            while (!cancelled && isTileLoadingRef.current && Date.now() - startedAt < MAX_WAIT_MS) {
                await wait(POLL_MS);
            }
        };

        (async () => {
            const frameStartedAt = Date.now();
            await waitForFrameReady();
            if (cancelled) return;
            const elapsed = Date.now() - frameStartedAt;
            await wait(Math.max(0, MIN_FRAME_MS - elapsed));
            if (cancelled) return;
            setCurrentIndex((prev) => {
                const total = timestamps.length;
                return total > 0 ? (prev + 1) % total : 0;
            });
        })();

        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    // Re-running this on every currentIndex change is the point: each frame
    // gets its own fresh wait-then-advance cycle while playing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlaying, currentIndex, selectedBand, apiInfo]);

    const satellites = apiInfo
        ? Array.isArray(apiInfo.satellite)
            ? apiInfo.satellite
            : apiInfo.satellite ? [apiInfo.satellite] : []
        : [];

    const bands = apiInfo?.bands || {};

    // Derive timestamps and full_path for the selected band
    const selectedBandData = (apiInfo?.data || {})[selectedBand] || {};
    const timestamps = Array.isArray(selectedBandData.timestamps) ? selectedBandData.timestamps : [];
    const fullPaths = Array.isArray(selectedBandData.full_path) ? selectedBandData.full_path : [];
    console.log("Selected band:", selectedBand, "| Band data:", selectedBandData, "| timestamps:", timestamps, "| fullPaths:", fullPaths);

    // Fetch real statistics for the first file of the selected band
    useEffect(() => {
        if (!fullPaths[0]) return;
        const statsUrl = `${RTILES_BASE_URL}/cog/statistics?url=${encodeURIComponent(fullPaths[0])}`;
        fetch(statsUrl)
            .then((r) => r.ok ? r.json() : null)
            .then((stats) => {
                console.log("COG statistics:", stats);
                setBandStats(stats);
            })
            .catch((err) => console.error("COG stats error:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedBand, fullPaths[0]]);

    const selectStyle = {
        fontSize: "13px",
        padding: "6px 10px",
        borderRadius: "6px",
        border: "1px solid #ced4da",
        outline: "none",
        backgroundColor: "#fff",
        cursor: "pointer",
    };

    const currentFullPath = fullPaths[currentIndex] || null;

    const buildCogTileUrl = (filePath) => {
        if (!filePath) return null;
        const encoded = encodeURIComponent(filePath);
        const isRgb = selectedBand.toLowerCase() === "rgb";

        if (isRgb) {
            const base = `${RTILES_BASE_URL}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}?format=jpeg&url=${encoded}`;
            if (bandStats) {
                const b1 = bandStats.b1 || bandStats["1"] || Object.values(bandStats)[0];
                const b2 = bandStats.b2 || bandStats["2"] || Object.values(bandStats)[1];
                const b3 = bandStats.b3 || bandStats["3"] || Object.values(bandStats)[2];
                if (b1 && b2 && b3) {
                    const r = [b1.percentile_2 ?? b1.min, b1.percentile_98 ?? b1.max];
                    const g = [b2.percentile_2 ?? b2.min, b2.percentile_98 ?? b2.max];
                    const b = [b3.percentile_2 ?? b3.min, b3.percentile_98 ?? b3.max];
                    return `${base}&rescale=${r[0]},${r[1]}&rescale=${g[0]},${g[1]}&rescale=${b[0]},${b[1]}&color_formula=Gamma+RGB+2.0+Saturation+3.0`;
                }
            }
            // Fallback — data is uint8 stored as 0–255
            return `${base}&rescale=0,255&rescale=0,255&rescale=0,255&color_formula=Gamma+RGB+2.0+Saturation+3.0`;
        }

        const bandNum = parseInt(selectedBand, 10);
        const BASE_TILE = `${RTILES_BASE_URL}/cog/tiles/WebMercatorQuad/{z}/{x}/{y}?format=jpeg&url=${encoded}`;

        if (bandNum === 8)  return `${BASE_TILE}&colormap=${WV_COLORMAP}`;
        if (bandNum === 13) return `${BASE_TILE}&colormap=${IR_COLORMAP}`;

        // Visible bands (03): MetStudio surface/vegetation colormap, jpeg, no rescale
        return `${BASE_TILE}&colormap=${VIS_COLORMAP}`;
    };

    const cogTileUrl = buildCogTileUrl(currentFullPath);

    // Every frame's tile URL for the current band, in order — what gets
    // warmed into the cache before playback starts. Memoized on the actual
    // path list (not the array reference, which is rebuilt every render) so
    // FramePreloader's effect — keyed on this — doesn't see a "new" array on
    // every progress tick and restart the whole preload loop from frame 0.
    const fullPathsKey = fullPaths.join('|');
    const preloadUrls = useMemo(
        () => fullPaths.map((p) => buildCogTileUrl(p)).filter(Boolean),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fullPathsKey, selectedBand, bandStats]
    );

    const handlePreloadProgress = useCallback((done, total) => {
        setPreloadProgress({ done, total });
    }, []);

    const handlePreloadComplete = useCallback((success) => {
        setPreloadActive(false);
        setIsPreloading(false);
        if (success) {
            // Remember this band's tiles are already warmed, so switching
            // away and back to it doesn't run the preload pass again — the
            // browser's tile cache still has everything from this run.
            if (pendingPreloadKeyRef.current) preloadedKeysRef.current.add(pendingPreloadKeyRef.current);
            setIsPlaying(true);
        }
    }, []);

    // Play click: with more than one frame, first silently preload every
    // frame's tiles (see FramePreloader), showing progress in place of the
    // play icon; playback only begins once that finishes, so every frame is
    // already cached and switching between them is instant. Pausing is
    // immediate either way, and cancels a preload still in progress. If this
    // exact band was already preloaded earlier in the session (the tiles are
    // still sitting in the cache from that run), skip straight to playback
    // instead of running through "Loading frames…" again.
    const handlePlayClick = () => {
        if (isPlaying) { setIsPlaying(false); return; }
        if (isPreloading) return;
        if (preloadUrls.length <= 1) { setIsPlaying(true); return; }
        const preloadKey = `${selectedBand}::${fullPathsKey}`;
        if (preloadedKeysRef.current.has(preloadKey)) {
            setIsPlaying(true);
            return;
        }
        pendingPreloadKeyRef.current = preloadKey;
        setPreloadProgress({ done: 0, total: preloadUrls.length });
        setIsPreloading(true);
        setPreloadActive(true);
    };

    return (
        <div
            className="satellite-imagery-container"
            ref={mapContainerRef}
            style={{
                ...(embedded ? { height: '100%', padding: 0, backgroundColor: 'transparent' } : {}),
                ...(isFullscreen ? { height: '100vh', width: '100vw' } : {}),
            }}
        >
            {/* Filter Bar — replaced by the floating <SatelliteFilterControl /> in the
                map's top-right corner (see below). Kept here, commented out, for reference.
            <Box
                sx={{
                    p: 2,
                    backgroundColor: "#ffffff",
                    borderRadius: "12px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.03)",
                    border: "1px solid #e8ecf1",
                    marginBottom: "12px",
                }}
            >
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <FilterAltIcon sx={{ color: "#0d6efd", fontSize: 22 }} />
                        <span style={{ fontWeight: 700, fontSize: "15px", color: "#333" }}>Filter</span>
                    </Box>

                    <Box sx={{ width: "1px", height: "36px", backgroundColor: "#e0e0e0" }} />

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <span style={{ fontWeight: 600, fontSize: "14px", color: "#333", whiteSpace: "nowrap" }}>Satellite:</span>
                        <select
                            value={selectedSatellite}
                            onChange={(e) => setSelectedSatellite(e.target.value)}
                            style={{ ...selectStyle, minWidth: "140px" }}
                        >
                            {satellites.length === 0 && <option value="">Loading...</option>}
                            {satellites.map((sat, i) => (
                                <option key={i} value={sat}>{sat}</option>
                            ))}
                        </select>
                    </Box>

                    <Box sx={{ width: "1px", height: "36px", backgroundColor: "#e0e0e0" }} />

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <span style={{ fontWeight: 600, fontSize: "14px", color: "#333", whiteSpace: "nowrap" }}>Layer:</span>
                        <select
                            value={selectedBand}
                            onChange={(e) => setSelectedBand(e.target.value)}
                            style={{ ...selectStyle, minWidth: "200px" }}
                        >
                            {Object.keys(bands).length === 0 && <option value="">Loading...</option>}
                            {Object.entries(bands).map(([key, band]) => (
                                <option key={key} value={key}>{band?.description || key}</option>
                            ))}
                        </select>
                    </Box>
                </Box>
            </Box>
            */}

            {/* Map */}
            <div style={{ flex: 1, borderRadius: embedded ? 0 : (timestamps.length > 0 ? "12px 12px 0 0" : "12px"), overflow: "hidden", minHeight: "400px", position: "relative" }}>
                {isTileLoading && (
                    <div style={{
                        position: "absolute", inset: 0, zIndex: 1002,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        background: "rgba(255,255,255,0.35)", pointerEvents: "none",
                    }}>
                        <div className="tile-spinner" />
                    </div>
                )}
                {/* Fullscreen Toggle Button */}
                <button
                    onClick={toggleFullscreen}
                    style={{
                        position: 'absolute',
                        top: '120px',
                        left: '10px',
                        zIndex: 1000,
                        background: 'rgba(255, 255, 255, 0.9)',
                        border: '2px solid rgba(0, 0, 0, 0.2)',
                        borderRadius: '4px',
                        width: '34px',
                        height: '34px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#333',
                        fontSize: '18px',
                        boxShadow: 'none',
                        transition: 'all 0.2s ease',
                    }}
                    title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                    {isFullscreen ? '✕' : '⛶'}
                </button>
                <MapContainer
                    center={TIMOR_CENTER}
                    zoom={TIMOR_ZOOM}
                    style={{ width: "100%", height: "100%" }}
                    attributionControl={false}
                >
                  <MapHomeControl />
                  <MapInfoControl
                    text="Latest satellite imagery over the Timor-Leste region for monitoring cyclones and weather systems."
                    width="380px"
                  />
                  <SatelliteFilterControl
                    satellites={satellites}
                    selectedSatellite={selectedSatellite}
                    onSatelliteChange={(e) => setSelectedSatellite(e.target.value)}
                    bands={bands}
                    selectedBand={selectedBand}
                    onBandChange={(e) => setSelectedBand(e.target.value)}
                    selectStyle={selectStyle}
                  />
                    <LayersControl position="topright">
                        <LayersControl.BaseLayer name="Satellite" checked>
                            <TileLayer
                                url="https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}"
                                attribution="Map data © Google"
                            />
                        </LayersControl.BaseLayer>
                        <LayersControl.BaseLayer name="OpenStreetMap">
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution="© OpenStreetMap contributors"
                            />
                        </LayersControl.BaseLayer>
                        {boundaryData && (
                            <LayersControl.Overlay name="Timor-Leste Boundary" checked>
                                <GeoJSON
                                    data={boundaryData}
                                    style={{ color: "#2cdf59", weight: 1.5, fillOpacity: 0 }}
                                />
                            </LayersControl.Overlay>
                        )}
                    </LayersControl>

                    <TileLoadTracker onLoading={handleTileLoading} onLoaded={handleTileLoaded} />

                    {/* COG satellite imagery tile layer — cached to respect 20 RPM limit */}
                    {cogTileUrl && (
                        <CachedTileLayer
                            url={cogTileUrl}
                            attribution="Himawari-9 © MetStudio"
                            opacity={1}
                            zIndex={500}
                        />
                    )}

                    {/* Warms every frame's tiles before Play starts (see handlePlayClick) */}
                    <FramePreloader
                        urls={preloadUrls}
                        active={preloadActive}
                        onProgress={handlePreloadProgress}
                        onComplete={handlePreloadComplete}
                    />
                </MapContainer>
            </div>

            {/* Timestamp strip — only shown when timestamps are available */}
            {timestamps.length > 0 && (
                <div style={{
                    backgroundColor: "#ffffff",
                    borderTop: "1px solid #f1f3f5",
                    borderRadius: "0 0 12px 12px",
                    padding: "10px 20px",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "12px",
                }}>
                    {/* Play/Pause — shows a spinner while every frame preloads */}
                    <button
                        onClick={handlePlayClick}
                        disabled={isPreloading}
                        title={isPreloading ? "Loading every frame before playback…" : undefined}
                        style={{
                            width: "2.2rem", height: "2.2rem",
                            backgroundColor: isPreloading ? "#93c5fd" : "#0d6efd", color: "white",
                            borderRadius: "50%", border: "none",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: isPreloading ? "default" : "pointer", flexShrink: 0,
                            boxShadow: "0 2px 6px rgba(13,110,253,0.3)",
                        }}
                    >
                        {isPreloading
                            ? <div className="tile-spinner-sm" />
                            : isPlaying ? <Pause size={14} /> : <Play size={14} style={{ marginLeft: "2px" }} />}
                    </button>

                    {/* Prev */}
                    <IconButton
                        size="small"
                        onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                        disabled={currentIndex === 0}
                        sx={{ color: currentIndex === 0 ? "#cbd5e1" : "#0d6efd", p: "6px" }}
                    >
                        <FontAwesomeIcon icon={faArrowCircleLeft} style={{ fontSize: "22px" }} />
                    </IconButton>

                    {/* Timestamp dropdown */}
                    <select
                        value={currentIndex}
                        onChange={(e) => {
                            setIsPlaying(false);
                            setCurrentIndex(Number(e.target.value));
                        }}
                        style={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#1e293b",
                            background: "#f8fafc",
                            borderRadius: "10px",
                            padding: "7px 14px",
                            border: "1px solid #e2e8f0",
                            minWidth: "220px",
                            cursor: "pointer",
                            outline: "none",
                        }}
                    >
                        {timestamps.map((ts, idx) => (
                            <option key={idx} value={idx}>
                                {formatTimestamp(ts)}
                            </option>
                        ))}
                    </select>

                    {/* Next */}
                    <IconButton
                        size="small"
                        onClick={() => setCurrentIndex((i) => Math.min(timestamps.length - 1, i + 1))}
                        disabled={currentIndex === timestamps.length - 1}
                        sx={{ color: currentIndex === timestamps.length - 1 ? "#cbd5e1" : "#0d6efd", p: "6px" }}
                    >
                        <FontAwesomeIcon icon={faArrowCircleRight} style={{ fontSize: "22px" }} />
                    </IconButton>

                    {/* Counter — shows preload progress while frames are being warmed up */}
                    <span style={{ fontSize: "12px", color: isPreloading ? "#0d6efd" : "#94a3b8", fontWeight: 500, whiteSpace: "nowrap" }}>
                        {isPreloading
                            ? `Loading frames ${preloadProgress.done} / ${preloadProgress.total}…`
                            : `${currentIndex + 1} / ${timestamps.length}`}
                    </span>
                </div>
            )}
        </div>
    );
};

export default SatelliteImagery;
